import { ActionExecutionEventType } from '@shared/generated/graphql';
import { QueryParamName } from 'clerk_common/constants/urls/queryParams';
import { isEmpty } from 'lodash';
import { parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';

export type VisSetting = {
  visibleActionExecutionEvents?: ActionExecutionEventType[];
};

export type ShouldShowType = ActionExecutionEventType;

type UseTaskVisSettingsValue = {
  visSettings: VisSetting;
  setVisSettings: (visSettings: VisSetting) => Promise<void>;
  shouldShow: (t: ShouldShowType) => boolean;
};

const DEFAULT_VIS_SETTINGS: VisSetting = {
  visibleActionExecutionEvents: [
    ActionExecutionEventType.AGENT_REASONING,
    ActionExecutionEventType.TOOL_USED,
    ActionExecutionEventType.ESCALATION,
    ActionExecutionEventType.MEMORY_RECALLED,
    ActionExecutionEventType.UNKNOWN,
    ActionExecutionEventType.DECISION_EDGE_EVALUATED,
    ActionExecutionEventType.SPAM_GUARD,
    ActionExecutionEventType.REMINDER,
    ActionExecutionEventType.INSTRUCTION_MESSAGE,
  ],
};

export const DEFAULT_CHAT_VIS_SETTINGS: VisSetting = {
  visibleActionExecutionEvents: [ActionExecutionEventType.ESCALATION],
};

export function useTaskVisSettings(
  defaultVisSettings: VisSetting = DEFAULT_VIS_SETTINGS
): UseTaskVisSettingsValue {
  const [queryParams, setQueryParams] = useQueryStates({
    [QueryParamName.TASK_VISUALIZATION_SETTINGS]: parseAsString,
  });

  const serializedVisSettings =
    queryParams[QueryParamName.TASK_VISUALIZATION_SETTINGS];
  const visSettings = useMemo(
    () => parseOrSetDefault(serializedVisSettings, defaultVisSettings),
    [defaultVisSettings, serializedVisSettings]
  );

  const setVisSettings = useCallback(
    async (updatedVisSettings: VisSetting) => {
      await setQueryParams({
        [QueryParamName.TASK_VISUALIZATION_SETTINGS]:
          JSON.stringify(updatedVisSettings),
      });
    },
    [setQueryParams]
  );

  const shouldShow = useCallback(
    (t: ShouldShowType) =>
      visSettings.visibleActionExecutionEvents?.includes(t) ?? false,
    [visSettings.visibleActionExecutionEvents]
  );

  return useMemo(
    () => ({
      visSettings,
      setVisSettings,
      shouldShow,
    }),
    [setVisSettings, shouldShow, visSettings]
  );
}

function parseOrSetDefault(
  queryParam: string | null,
  defaultVisSettings: VisSetting
): VisSetting {
  if (!queryParam) return defaultVisSettings;
  const visSettings = JSON.parse(queryParam) as VisSetting;
  if (isEmpty(visSettings)) return defaultVisSettings;
  return visSettings;
}
